import React from 'react';
import throttle from 'lodash/throttle';
import { Modal } from 'antd';
import { logoutUser } from 'redux/auth/actions';
import Client from 'App/client';
import Timer from '../utils/timer';
import { getCurrentSession } from './auth';

const minutesToMillis = mins => mins * 60 * 1000;

/** Stable ids referenced from the modal wrap’s aria-labelledby (see applyIdleSessionModalA11y). */
const IDLE_SESSION_TITLE_ID = 'idle-session-modal-title';
const IDLE_SESSION_DESC_ID = 'idle-session-modal-description';

const sessionExpiringMessage =
  "Your session is expiring due to inactivity. To continue with your session, select the ‘Continue' button below. Or select the 'Log Out' button to end your session. If no action is taken, any unsaved data will be lost.";

const sessionTimeoutModalContent = (secondsRemaining) => {
  return (
    <div>
      <p id={IDLE_SESSION_DESC_ID}>{sessionExpiringMessage}</p>
      <p>{`${secondsRemaining} seconds remaining.`}</p>
    </div>
  );
};

const logout = () => {
  Client.getStore().dispatch(logoutUser());
};

const sessionRefresh = throttle(async () => {
  const session = await getCurrentSession();
  if (!session) {
    logout();
  }
}, 15 * 1000);

const resetTimer = async () => {
  sessionRefresh();
  Timer.start({
    key: 'active',
    cb: () => {
      /* eslint-disable-next-line */
      let secondsToGo = 20;

      /** Focus the dialog wrap once per open so SRs read the alert text before toolbar buttons (antd defaults to autoFocus on OK). */
      let focusedIdleModalWrap = false;

      /**
       * rc-dialog renders the wrap with role="dialog" and no aria-labelledby (confirm passes title="").
       * The OK button is auto-focused by default, so NVDA/VoiceOver often only announce the button.
       * We set alertdialog + aria-labelledby (title + body ids), remove the inner panel role="document",
       * and focus the wrap once so name/description are announced with the dialog.
       */
      const applyIdleSessionModalA11y = () => {
        const panel = document.querySelector('.ant-modal.idle-session-modal');
        if (!panel) return;
        const wrap = panel.closest('.ant-modal-wrap');
        if (!wrap) return;

        wrap.setAttribute('role', 'alertdialog');
        wrap.setAttribute('aria-modal', 'true');
        wrap.setAttribute(
          'aria-labelledby',
          `${IDLE_SESSION_TITLE_ID} ${IDLE_SESSION_DESC_ID}`,
        );
        wrap.removeAttribute('aria-describedby');

        panel.removeAttribute('role');

        if (!focusedIdleModalWrap) {
          wrap.focus({ preventScroll: true });
          focusedIdleModalWrap = true;
        }
      };

      const scheduleIdleSessionModalA11y = () => {
        applyIdleSessionModalA11y();
        requestAnimationFrame(() => {
          applyIdleSessionModalA11y();
          setTimeout(applyIdleSessionModalA11y, 0);
        });
      };

      document.body.removeEventListener('click', resetTimer);

      /* set up modal — autoFocusButton: null prevents jumping focus to Continue before SR reads context */
      const modal = Modal.info({
        title: (
          <span id={IDLE_SESSION_TITLE_ID} role="heading" aria-level={2}>
            Session Ending Soon
          </span>
        ),
        content: sessionTimeoutModalContent(secondsToGo),
        okText: "Continue",
        cancelText: 'Log Out',
        okCancel: true,
        okType: 'default',
        autoFocusButton: null,
        className: 'idle-session-modal',
        maskClosable: false,
        okButtonProps: {
          size: 'large',
          type: 'default',
        },
        cancelButtonProps: {
          size: 'large',
          type: 'default',
        },
      });

      scheduleIdleSessionModalA11y();

      /* every second, we will update the content */
      const timer = setInterval(() => {
        secondsToGo -= 1;
        modal.update({
          content: sessionTimeoutModalContent(secondsToGo),
        });
      }, 1000);

      /* if no response from user, this timeout will occur */
      const timeout = setTimeout(() => {
        clearInterval(timer);
        Timer.clear('active');
        modal.destroy();
        logout();
      }, secondsToGo * 1000);

      /* if user presses ok, we'll clear this timeout */
      const onUserActivity = () => {
        clearTimeout(timeout);
        clearInterval(timer);
        resetTimer();
        document.body.addEventListener('click', resetTimer);
      };

      const onLogoutClick = () => {
        clearTimeout(timeout);
        clearInterval(timer);
        Timer.clear('active');
        logout();
      };

      /* we'll update the modal here, so we have reference to the timer */
      modal.update({
        onOk: onUserActivity,
        onCancel: onLogoutClick,
      });
    },
    expiresIn: minutesToMillis(30),
  });
};

export default {
  init() {
    resetTimer();
    document.body.addEventListener('click', resetTimer);
  },
  cleanUp() {
    Timer.clear('active');
    document.body.removeEventListener('click', resetTimer);
  },
};
